import { useState } from "react";
import { Box, Button, Modal, Typography, Divider, CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";

import ManualModelDetails from "./ManualModelDetails";
import ModelPreview from "./ModelPreview";
import { editUnit } from "../../../../../services/apis";

const getModalStyle = () => ({
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 590 },
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  maxHeight: "95vh",
  overflowY: "auto",
});

export default function HeatingEnergyModel({
  openHeatingEnergyModel,
  setOpenHeatingEnergyModel,
  baselineDailySummaryData,
  unit,
  manualModelDetails,
  setManualModelDetails,
  setAlertState,
  setEnableSearchQuery,
}: any) {
  const [isLoading, setIsLoading] = useState(false);
  const [enableButton, setEnableButton] = useState(true);

  const cleanPayload = (payload: Record<string, any>) => {
    return Object.fromEntries(Object.entries(payload).filter(([_, value]) => value !== ""));
  };

  const handleValidationChange = (valid: boolean) => {
    setEnableButton(valid);
  };

  const handleClose = () => {
    setEnableButton(true);
    setOpenHeatingEnergyModel(false);
    setManualModelDetails((prev: any) => ({
      ...prev,
      ch: unit?.ch || 0,
      thcp: unit?.thcp || 0,
      bh: unit?.bh || 0,
    }));
  };

  const handleSubmitAllInfo = async () => {
    setIsLoading(true);
    setEnableButton(false);
    editUnitMutation.mutate(
      cleanPayload({
        unit_id: unit?.unit_id,
        unit_name: unit?.unit_name,
        hvac_serial_num: unit?.unit_serial_num,
        hvac_make: unit?.unit_brand,
        hvac_model_number: unit?.unit_model_number,
        tonnage: unit?.tonnage,
        main_voltage: unit?.main_voltage,
        pace_device_serial_number: unit?.unit_serial_num,
        main_phases: unit?.main_phases,
        stage1_btuh: unit?.stage1_btuh,
        stage2_btuh: unit?.stage2_btuh,
        cc: manualModelDetails?.cc,
        bc: manualModelDetails?.bc,
        tcp: manualModelDetails?.tccp,
        ch: manualModelDetails?.ch,
        bh: manualModelDetails?.bh,
        thcp: manualModelDetails?.thcp,
      })
    );
  };

  const editUnitMutation = useMutation({
    mutationFn: editUnit,
    onSuccess: (data) => {
      console.log("Edit Unit successfully:", data);
      setIsLoading(false);
      setEnableSearchQuery(true);
      handleClose();
      setAlertState({
        isAlert: true,
        severity: "success",
        title: "",
        description: "Heating Energy Model updated.",
        resetOpen: true,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (error) => {
      console.error("Error Edit Unit :", error);
      setIsLoading(false);
      handleClose();
      setAlertState({
        isAlert: true,
        severity: "error",
        title: "",
        description: "Heating Energy Model was unable to be saved. Please try again later.",
        resetOpen: true,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
  });

  // console.log("unit", unit);
  return (
    <Modal open={openHeatingEnergyModel} onClose={handleClose}>
      <Box sx={getModalStyle}>
        <Box sx={{ padding: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Heating Energy Model
          </Typography>
        </Box>
        <Box sx={{ padding: 3, paddingTop: 0 }}>
          <ManualModelDetails
            onValidationChange={handleValidationChange}
            baselineDailySummaryData={baselineDailySummaryData}
            manualModelDetails={manualModelDetails}
            setManualModelDetails={setManualModelDetails}
            energyModel={"heating"}
          />
          <Divider sx={{ my: 2 }} />
          <ModelPreview baselineDailySummaryData={baselineDailySummaryData} manualModelDetails={manualModelDetails} energyModel={"heating"} />
          <Divider sx={{ my: 2 }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 3 }}>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={isLoading || !enableButton} // Disable button while loading
            variant="contained"
            onClick={handleSubmitAllInfo}
            startIcon={isLoading ? <CircularProgress size={20} /> : null} // Add loader
          >
            {isLoading ? "Saving..." : "Save"} {/* Change text while loading */}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
